import { useEffect, useState } from "react";
import { IFormSection, TSubmitType } from "../../types/common";
import {
  API_CHARTS_OF_ACCOUNTS_ALL,
  API_LEDGERS,
  API_LOAN_APPLICATIONS,
} from "../../utils/apis";
import useGet from "../common/useGet";
import { getResultOptions } from "../../utils/helpers";
import { DISBURSED } from "../../utils/constants";

export type TFormCustomProps = {
  id?: number;
  submitType?: TSubmitType;
};

export const useFinesForm = ({ form, updateFormValue, updateFormValues }) => {
  const [numberPlate, setNumberPlate] = useState(form?.number_plate || "");
  const {
    loadData: fetchLoanApplication,
    data: loanApplicationData,
    success: loanApplicationSuccess,
  } = useGet();
  const getLoanApplication = () => {
    if (numberPlate) {
      fetchLoanApplication({
        api: `${API_LOAN_APPLICATIONS}`,
        params: { number_plate: numberPlate },
      });
    }
  };
  useEffect(() => {
    if (loanApplicationSuccess && loanApplicationData?.results?.length > 0) {
      updateFormValues({
        client_name: `${loanApplicationData?.results[0]?.first_name || ""} ${loanApplicationData?.results[0]?.last_name || ""}`,
        object_id: loanApplicationData?.results[0]?.id,
      });
    }
  }, [loanApplicationSuccess, loanApplicationData]);

  useEffect(() => {
    updateFormValue("amount", 50000);
  }, []);

  const sections: IFormSection[] = [
    {
      title: "Fines Information",
      fields: [
        {
          field_name: "transaction_date",
          type: "date",
          label: "Transaction Date",
        },
        {
          field_name: "debit_account",
          type: "select",
          label: "Debit Account Type",
          options_name: "accounts",
          props: { required: true },
        },
        {
          field_name: "amount",
          type: "number",
          label: "Debit Amount",
          props: { required: true },
        },
        {
          field_name: "credit_account",
          type: "select",
          label: "Credit Account Type",
          options_name: "credit_accounts",
          props: { required: true },
        },
        {
          field_name: "number_plate",
          type: "select",
          label: "Number Plate",
          props: {
            required: true,
            onInput: (e) => setNumberPlate(e.target.value),
            onChange: (value) => {
              updateFormValue("number_plate", value);
              getLoanApplication();
            },
          },
          options_name: "number_plates",
        },
        {
          field_name: "client_name",
          type: "input",
          label: "Client Name",
          props: { required: true, disabled: true },
          visible: form?.number_plate !== undefined,
        },
        {
          field_name: "description",
          type: "textarea",
          label: "Description",
          props: { required: true, wide: true },
        },
      ],
    },
  ];

  const redirect = `/reports/fines`;

  const getConfigOptions = () => {
    const { loadData: getAccounts, data: accounts } = useGet();
    const { loadData: getCreditAccounts, data: creditAccounts } = useGet();
    const { loadData: getNumberPlates, data: numberPlates } = useGet();

    useEffect(() => {
      getAccounts({
        api: API_CHARTS_OF_ACCOUNTS_ALL,
        params: {},
      });
      getCreditAccounts({
        api: API_CHARTS_OF_ACCOUNTS_ALL,
        params: { is_fines_account: true },
      });
    }, []);

    useEffect(() => {
      if (numberPlate) {
        getNumberPlates({
          api: `${API_LOAN_APPLICATIONS}`,
          params: { number_plate: numberPlate, status: DISBURSED },
        });
      }
    }, [numberPlate]);

    const getNumberPlateOptions = (
      data: any[] | Record<string, any>,
      key?: string | undefined | null,
    ) => {
      const results = Array.isArray(data) ? data : data?.results || [];
      return results.length
        ? results.map((item: any) => ({
            id: key ? item[key] : item.id,
            name: `${item?.number_plate} (${item?.first_name} ${item?.last_name})`.trim(),
            data: item,
          }))
        : [];
    };

    return {
      accounts: getResultOptions(accounts, "account_name"),
      credit_accounts: getResultOptions(creditAccounts, "account_name"),
      number_plates: getNumberPlateOptions(numberPlates, "number_plate"),
    };
  };

  const options = getConfigOptions();

  return { sections, redirect, options };
};

export function submitFines(payload: any, id: number | null) {
  //  on update
  if (id) {
    return {
      id,
      api: API_LEDGERS,
      params: payload,
    };
  }
  // on create
  return {
    api: API_LEDGERS,
    params: payload,
  };
}
